import { api } from '../lib/api'
import type {
  OwnerConsoleData,
  RangeKey,
  SegmentKey,
  TabConfig,
  KpiMetric,
  HealthItem,
} from '../routes/settings/ownerConsoleTypes'

type OwnerConsoleApiResponse = {
  range: RangeKey
  segment: SegmentKey
  compare: boolean
  kpis: Array<{
    id: string
    label: string
    value: string
    deltaPct?: number | null
    trend: number[]
    notes: string[]
  }>
  health: Array<{
    id: string
    label: string
    value: string
    status: 'ok' | 'warn' | 'bad'
  }>
  tabs: Array<{
    key: TabConfig['key']
    label: string
    chartTitle: string
    chartSeries: number[][]
    chartLegend?: string[] | null
    tableTitle: string
    cards: Array<{
      title: string
      value: string
      deltaPct?: number | null
      subtext?: string | null
    }>
    table: {
      columns: string[]
      rows: string[][]
    }
    dailyTitle?: string | null
    daily?: {
      columns: string[]
      rows: string[][]
    } | null
    leaderboardTitle?: string | null
    leaderboard?: {
      columns: string[]
      rows: string[][]
    } | null
  }>
}

const normalizeKpis = (kpis: OwnerConsoleApiResponse['kpis']): KpiMetric[] =>
  kpis.map((kpi) => ({
    id: kpi.id,
    label: kpi.label,
    value: kpi.value,
    deltaPct: kpi.deltaPct ?? undefined,
    trend: kpi.trend ?? [],
    notes: kpi.notes ?? [],
  }))

const normalizeHealth = (health: OwnerConsoleApiResponse['health']): HealthItem[] =>
  health.map((item) => ({
    id: item.id,
    label: item.label,
    value: item.value,
    status: item.status,
  }))

const normalizeTabs = (tabs: OwnerConsoleApiResponse['tabs']): TabConfig[] =>
  tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    chartTitle: tab.chartTitle,
    chartSeries: tab.chartSeries ?? [],
    chartLegend: tab.chartLegend ?? undefined,
    tableTitle: tab.tableTitle,
    cards: tab.cards.map((card) => ({
      title: card.title,
      value: card.value,
      deltaPct: card.deltaPct ?? undefined,
      subtext: card.subtext ?? undefined,
    })),
    table: {
      columns: tab.table.columns ?? [],
      rows: tab.table.rows ?? [],
    },
    dailyTitle: tab.dailyTitle ?? undefined,
    daily: tab.daily ?? undefined,
    leaderboardTitle: tab.leaderboardTitle ?? undefined,
    leaderboard: tab.leaderboard ?? undefined,
  }))

export async function fetchOwnerConsole(params: {
  range: RangeKey
  segment: SegmentKey
  compare: boolean
  creatorId?: string
  from?: string
  to?: string
}): Promise<OwnerConsoleData> {
  const { data } = await api.get<OwnerConsoleApiResponse>('/api/owner/console/', {
    params: {
      range: params.range,
      segment: params.segment,
      compare: params.compare ? 1 : 0,
      creator_id: params.creatorId || undefined,
      from: params.from,
      to: params.to,
    },
  })

  return {
    range: data.range,
    segment: data.segment,
    compare: data.compare,
    kpiMetrics: normalizeKpis(data.kpis ?? []),
    healthItems: normalizeHealth(data.health ?? []),
    tabConfigs: normalizeTabs(data.tabs ?? []),
  }
}
